import { afterNextRender, Component, contentChild, ElementRef, input, InputSignal, output, signal, Signal, WritableSignal, inject, OnDestroy, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { EventType, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ModalFooterComponent } from './modal-footer/modal-footer.component';
import { ModalBodyComponent } from './modal-body/modal-body.component';

/**
 * This is a standalone modal-dialog window popup that is controlled by the IsShown input signal.
 * Content is either projected between `<app-modal>` tags or passed as a template via `ModalService`.
 */
@Component({
  selector: 'app-modal',
  imports: [NgTemplateOutlet],
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './modal.component.scss'
})
export class ModalComponent implements OnDestroy {
  private readonly ElementRef = inject(ElementRef);
  private readonly Router = inject(Router);
  private RouterEventsSubscription: Subscription;
  private LastNavigatedPath = this.Router.url.split(/[?#]/)[0];
  private LastMouseDownOrTouchStartTargetElement: HTMLElement | undefined;
  private Body: Signal<ModalBodyComponent | undefined> = contentChild(ModalBodyComponent);
  private Footer: Signal<ModalFooterComponent | undefined> = contentChild(ModalFooterComponent);

  public IsShown = signal<boolean>(false);
  public Size = input<"small" | "medium" | "large" | "full">("small");
  public ShouldFadeInAndOut: InputSignal<boolean> = input<boolean>(true);
  public PreventClosing: InputSignal<boolean> = input<boolean>(false);
  public OnClosed = output<void>();
  public InterceptCloseRequests = input<boolean>(false);
  public OnCloseRequested = output<void>();
  public IsClosing: WritableSignal<boolean> = signal(false);
  // Template content set by ModalService.
  public Contents = input<TemplateRef<unknown> | undefined>(undefined);
  public ContentsContext = input<object | undefined>(undefined);

  public constructor(){
    document.body.append(this.ElementRef.nativeElement);
    // Close when navigating to a new page. 
    // Query param only changes are ignored, since some pages open their modals from query params (e.g. the calendars new booking modal).
    this.RouterEventsSubscription = this.Router.events.subscribe({
      next: routerEvent => {
        if (routerEvent.type === EventType.NavigationEnd) {
          const path = routerEvent.urlAfterRedirects.split(/[?#]/)[0];
          if (path !== this.LastNavigatedPath) {
            this.LastNavigatedPath = path;
            this.Close();
          }
        }
      }
    });
    // Hook events to any buttons with "close-button" classes on them.
    afterNextRender({
      read: () => {
        for (const closeButton of (this.ElementRef.nativeElement as HTMLElement).querySelectorAll(".close-button")){
          closeButton.addEventListener("click", () => {
            this.OnCloseClicked();
          });
        }
      }
    });
  }

  public ngOnDestroy(): void{
    this.RouterEventsSubscription.unsubscribe();
    this.ElementRef.nativeElement.remove();
  }

  public Open(): void{
    if (!this.IsShown()){
      this.IsShown.set(true);
      document.body.style.overflow = "hidden";
    }
  }

  public Close(): void{
    if (this.IsShown()){
      if (!this.ShouldFadeInAndOut()){
        if (!this.PreventClosing()){
          this.IsShown.set(false);
          this.OnClosed.emit();
          this.RemoveOverflowIfNoOtherModalsShown();
        }
      } else {
        if (!this.PreventClosing()){
          if (this.IsClosing() !== true){
            // It should fade out, then emit a close request when the animation ends
            this.IsClosing.set(true);
          }
        }
      }
    }
  }

  /**
   * When the modal is requested to be closed, usually in the form of clicking a close button of sorts.
   */
  public OnCloseClicked(): void{
    if (this.InterceptCloseRequests()){
      this.OnCloseRequested.emit();
    } else {
      this.Close();
    }
  }

  public OnAnimationEnded(e: AnimationEvent): void{
    if (e.animationName.endsWith("modal-fade-out")){
      this.IsClosing.set(false);
      this.IsShown.set(false);
      this.OnClosed.emit();
      this.RemoveOverflowIfNoOtherModalsShown();
    }
  }

  public OnMouseDown(e: MouseEvent): void{
    this.LastMouseDownOrTouchStartTargetElement = e.target as HTMLElement;
  }

  public OnTouchStart(e: TouchEvent): void{
    this.LastMouseDownOrTouchStartTargetElement = e.target as HTMLElement;
  }

  public OnMouseUp(e: MouseEvent): void{
    if (e.target === this.LastMouseDownOrTouchStartTargetElement){
      if (e.target === (this.ElementRef.nativeElement.querySelector(".modal-backdrop"))){
        this.OnCloseClicked();
      }
    }
  }

  public OnTouchEnd(e: TouchEvent): void{
    if (e.target === this.LastMouseDownOrTouchStartTargetElement){
      if (e.target === (this.ElementRef.nativeElement.querySelector(".modal-backdrop"))){
        this.OnCloseClicked();
      }
    }
  }

  /**
   * This code checks if any other modals are shown that are not the current modal element.
   * If so, then overflow on the body is not removed.
   */
  public RemoveOverflowIfNoOtherModalsShown(): void {
    setTimeout(() => {
      const backdrops = Array.from(document.querySelectorAll(".modal-backdrop"));
      for (const backdrop of backdrops){
        if (window.getComputedStyle(backdrop).display !== "none"){
          return;
        }
      }

      document.body.style.overflow = null!;
    }, 10);
  }
}